import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { calendarAPI } from '../lib/api';
import { getCategoryStyle, CATEGORY_STYLES } from '../lib/utils';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  GripVertical,
  Image as ImageIcon,
  Film,
  Layers,
  X,
  LayoutGrid,
  BookOpen,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const POST_TYPES = ['Carousel', 'Reel', 'Static'];
const POST_TYPE_ICONS = {
  Carousel: Layers,
  Reel: Film,
  Static: ImageIcon,
};

const getTitle = (item) => item.display_title || item.caption_used || item.idea_title || item.title || 'Untitled';

// ── Draggable post block (used in the unscheduled pool) ──
const DraggablePostBlock = ({ asset }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: asset.id,
    data: { asset },
  });
  const cat = getCategoryStyle(asset.category);
  const TypeIcon = POST_TYPE_ICONS[asset.post_type] || ImageIcon;
  const title = getTitle(asset);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`draggable-post-${asset.id}`}
      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-grab active:cursor-grabbing border select-none transition-all ${
        isDragging ? 'opacity-30 scale-95' : 'hover:shadow-md hover:-translate-y-0.5'
      } ${cat.bg} ${cat.text} border-current/10`}
      style={{ maxWidth: 180 }}
    >
      <GripVertical className="w-3 h-3 opacity-40 shrink-0" />
      <TypeIcon className="w-3 h-3 shrink-0" />
      <span className="truncate font-medium">{title.length > 20 ? title.slice(0, 20) + '...' : title}</span>
    </div>
  );
};

// ── Drag overlay (ghost that follows cursor) ──
const DragOverlayBlock = ({ asset }) => {
  if (!asset) return null;
  const cat = getCategoryStyle(asset.category);
  const TypeIcon = POST_TYPE_ICONS[asset.post_type] || ImageIcon;
  const title = getTitle(asset);
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border shadow-lg ${cat.bg} ${cat.text} border-current/10`}
      style={{ maxWidth: 180 }}
    >
      <TypeIcon className="w-3 h-3 shrink-0" />
      <span className="truncate font-medium">{title.length > 20 ? title.slice(0, 20) + '...' : title}</span>
    </div>
  );
};

// ── Draggable post block inside a calendar cell (for moving between dates) ──
const DraggableCellPost = ({ post, onRemove }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: post.id,
    data: { asset: post },
  });
  const cat = getCategoryStyle(post.category);
  const TypeIcon = POST_TYPE_ICONS[post.post_type] || ImageIcon;

  return (
    <div
      ref={setNodeRef}
      className={`group flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] leading-tight ${cat.bg} ${cat.text} ${
        isDragging ? 'opacity-30' : ''
      }`}
      data-testid={`cell-post-${post.id}`}
    >
      <span {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing flex items-center gap-0.5 flex-1 min-w-0">
        <TypeIcon className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{getTitle(post).length > 14 ? getTitle(post).slice(0, 14) + '..' : getTitle(post)}</span>
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(post.id); }}
        className="opacity-0 group-hover:opacity-100 ml-auto shrink-0 hover:text-red-600 transition-opacity"
        data-testid={`remove-post-${post.id}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
};

// ── Droppable calendar cell ──
const CalendarCell = ({ date, dateStr, posts, isToday, isInRange, onRemove }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${dateStr}`,
    data: { date: dateStr },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`calendar-cell-${dateStr}`}
      className={`border border-zinc-100 min-h-[76px] p-0.5 transition-colors flex flex-col ${
        isOver ? 'bg-amber-50/60 ring-1 ring-amber-300' : ''
      } ${isToday ? 'bg-[#D4AF37]/5' : ''} ${!isInRange ? 'opacity-30 bg-zinc-50' : 'bg-white'}`}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`text-xs font-medium mb-0.5 flex items-center justify-center rounded-full hover:bg-zinc-200 transition-colors px-1 ${
              isToday ? 'bg-[#D4AF37] text-white' : 'text-zinc-500'
            }`}
            data-testid={`date-trigger-${dateStr}`}
          >
            {date.getDate() === 1 ? (
              <span>{date.toLocaleDateString('en-US', { month: 'short' })} 1</span>
            ) : (
              <span>{date.getDate()}</span>
            )}
          </button>
        </PopoverTrigger>
        {posts.length > 0 && (
          <PopoverContent className="w-72 p-0" align="start" data-testid={`popover-${dateStr}`}>
            <div className="px-3 py-2 border-b border-zinc-100">
              <p className="text-xs font-semibold text-zinc-700">
                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {posts.length} post{posts.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="max-h-60 overflow-y-auto divide-y divide-zinc-50">
              {posts.map((p) => {
                const cat = getCategoryStyle(p.category);
                const TypeIcon = POST_TYPE_ICONS[p.post_type] || ImageIcon;
                return (
                  <div key={p.id} className="px-3 py-2 flex items-start gap-2" data-testid={`popover-item-${p.id}`}>
                    <div className={`w-1.5 h-full min-h-[32px] rounded-full ${cat.dot} shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-zinc-800 truncate">{getTitle(p)}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${cat.bg} ${cat.text}`}>
                          {p.category?.split(' ')[0]}
                        </span>
                        {p.post_type && (
                          <span className="text-[10px] text-zinc-400 flex items-center gap-0.5">
                            <TypeIcon className="w-2.5 h-2.5" /> {p.post_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onRemove(p.id)}
                      className="text-zinc-400 hover:text-red-500 transition-colors shrink-0 mt-0.5"
                      data-testid={`popover-remove-${p.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        )}
      </Popover>
      {/* Show post titles in cell — draggable + removable */}
      <div className="flex-1 space-y-0.5 overflow-hidden">
        {posts.slice(0, 3).map((p) => (
          <DraggableCellPost key={p.id} post={p} onRemove={onRemove} />
        ))}
        {posts.length > 3 && (
          <span className="text-[9px] text-zinc-400 pl-1">+{posts.length - 3} more</span>
        )}
      </div>
    </div>
  );
};

// ── Category counter bar ──
const CategoryCounters = ({ posts, label }) => {
  const counts = useMemo(() => {
    const c = {};
    posts.forEach((p) => {
      const cat = p.category || 'Uncategorized';
      c[cat] = (c[cat] || 0) + 1;
    });
    return c;
  }, [posts]);

  if (Object.keys(counts).length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid={`category-counters-${label}`}>
      {Object.entries(counts).map(([cat, count]) => {
        const style = getCategoryStyle(cat);
        return (
          <div key={cat} className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${style.bg} ${style.text}`}>
            <div className={`w-2 h-2 rounded-full ${style.dot}`} />
            <span className="font-medium">{cat.split('/')[0].trim()}</span>
            <span className="font-bold">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

// ── Range-based Calendar Grid builder (15th to 15th) ──
const buildRangeGrid = (startDate, endDate) => {
  // Find the Sunday on or before the start date
  const start = new Date(startDate);
  const dayOfWeek = start.getDay();
  const gridStart = new Date(start);
  gridStart.setDate(gridStart.getDate() - dayOfWeek);

  // Find the Saturday on or after the end date
  const end = new Date(endDate);
  const endDayOfWeek = end.getDay();
  const gridEnd = new Date(end);
  gridEnd.setDate(gridEnd.getDate() + (6 - endDayOfWeek));

  const days = [];
  const current = new Date(gridStart);
  while (current <= gridEnd) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Single calendar section (reused for Posts and Stories) ──
const CalendarSection = ({ title, icon: Icon, scheduled, unscheduled, onDrop, onUnschedule, rangeDates }) => {
  const days = useMemo(() => {
    const start = new Date(rangeDates.start + 'T00:00:00');
    const end = new Date(rangeDates.end + 'T00:00:00');
    return buildRangeGrid(start, end);
  }, [rangeDates]);
  const todayStr = toDateStr(new Date());
  const [activeAsset, setActiveAsset] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Group scheduled by date
  const byDate = useMemo(() => {
    const m = {};
    scheduled.forEach((a) => {
      if (a.posting_date) {
        if (!m[a.posting_date]) m[a.posting_date] = [];
        m[a.posting_date].push(a);
      }
    });
    return m;
  }, [scheduled]);

  const handleDragStart = (event) => {
    setActiveAsset(event.active.data.current?.asset || null);
  };

  const handleDragEnd = (event) => {
    setActiveAsset(null);
    const { active, over } = event;
    if (!over) return;
    const targetDate = over.data.current?.date;
    if (!targetDate) return;
    const asset = active.data.current?.asset;
    if (!asset) return;
    onDrop(asset.id, targetDate);
  };

  // Range label: "Mar 15 – Apr 15, 2025"
  const startD = new Date(rangeDates.start + 'T00:00:00');
  const endD = new Date(rangeDates.end + 'T00:00:00');
  const rangeLabel = `${startD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endD.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="bg-white border border-zinc-100 rounded-sm" data-testid={`calendar-section-${title.toLowerCase()}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-zinc-500" />
          <h2 className="text-base font-semibold text-zinc-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            {title} Calendar
          </h2>
          <span className="text-xs text-zinc-400 ml-1">({scheduled.length} scheduled)</span>
        </div>
        <span className="text-sm font-medium text-zinc-700">
          {rangeLabel}
        </span>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Unscheduled pool */}
        {unscheduled.length > 0 && (
          <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/50" data-testid={`${title.toLowerCase()}-unscheduled-pool`}>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1.5">
              Unscheduled ({unscheduled.length}) — drag to calendar
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-[72px] overflow-y-auto">
              {unscheduled.map((a) => (
                <DraggablePostBlock key={a.id} asset={a} />
              ))}
            </div>
          </div>
        )}

        {/* Category counters - only scheduled (on-calendar) posts */}
        <div className="px-4 py-2 border-b border-zinc-100">
          <CategoryCounters posts={scheduled} label={title.toLowerCase()} />
        </div>

        {/* Calendar grid */}
        <div className="px-2 py-2">
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-[10px] text-center uppercase tracking-widest text-zinc-400 font-semibold py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const ds = toDateStr(d);
              const inRange = ds >= rangeDates.start && ds <= rangeDates.end;
              return (
                <CalendarCell
                  key={ds}
                  date={d}
                  dateStr={ds}
                  posts={byDate[ds] || []}
                  isToday={ds === todayStr}
                  isInRange={inRange}
                  onRemove={onUnschedule}
                />
              );
            })}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          <DragOverlayBlock asset={activeAsset} />
        </DragOverlay>
      </DndContext>
    </div>
  );
};

// ══════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════
const ContentCalendar = () => {
  const { activeBrand } = useAuth();
  const [data, setData] = useState({
    scheduled_posts: [],
    scheduled_stories: [],
    unscheduled_posts: [],
    unscheduled_stories: [],
  });
  const [loading, setLoading] = useState(true);

  const rangeDates = { start: '2025-03-15', end: '2025-04-15' };

  const fetchData = useCallback(async () => {
    if (!activeBrand) return;
    try {
      setLoading(true);
      const res = await calendarAPI.getData({}, activeBrand);
      setData(res.data);
    } catch {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDrop = async (assetId, dateStr) => {
    // Optimistic update
    setData((prev) => {
      const allPools = [...prev.unscheduled_posts, ...prev.scheduled_posts, ...prev.unscheduled_stories, ...prev.scheduled_stories];
      const asset = allPools.find((a) => a.id === assetId);
      if (!asset) return prev;
      const updated = { ...asset, posting_date: dateStr };
      const isStory = asset.content_type === 'story';
      return {
        ...prev,
        unscheduled_posts: isStory ? prev.unscheduled_posts : prev.unscheduled_posts.filter((a) => a.id !== assetId),
        unscheduled_stories: isStory ? prev.unscheduled_stories.filter((a) => a.id !== assetId) : prev.unscheduled_stories,
        scheduled_posts: isStory
          ? prev.scheduled_posts
          : [...prev.scheduled_posts.filter((a) => a.id !== assetId), updated],
        scheduled_stories: isStory
          ? [...prev.scheduled_stories.filter((a) => a.id !== assetId), updated]
          : prev.scheduled_stories,
      };
    });
    try {
      await calendarAPI.schedule(assetId, dateStr);
      toast.success('Post scheduled');
    } catch {
      toast.error('Failed to schedule');
      fetchData();
    }
  };

  const handleUnschedule = async (packId) => {
    // Optimistic update — move from scheduled back to unscheduled
    setData((prev) => {
      const allScheduled = [...prev.scheduled_posts, ...prev.scheduled_stories];
      const pack = allScheduled.find((a) => a.id === packId);
      if (!pack) return prev;
      const unscheduled = { ...pack, posting_date: null };
      const isStory = pack.content_type === 'story';
      return {
        ...prev,
        scheduled_posts: isStory ? prev.scheduled_posts : prev.scheduled_posts.filter((a) => a.id !== packId),
        scheduled_stories: isStory ? prev.scheduled_stories.filter((a) => a.id !== packId) : prev.scheduled_stories,
        unscheduled_posts: isStory ? prev.unscheduled_posts : [...prev.unscheduled_posts, unscheduled],
        unscheduled_stories: isStory ? [...prev.unscheduled_stories, unscheduled] : prev.unscheduled_stories,
      };
    });
    try {
      await calendarAPI.unschedule(packId);
      toast.success('Removed from calendar');
    } catch {
      toast.error('Failed to remove');
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="calendar-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="h-[500px] bg-zinc-100 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-in" data-testid="content-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Content Calendar
          </h1>
          <p className="text-zinc-400 text-xs mt-0.5">
            Launch window: Mar 15 – Apr 15 &middot; Drag unscheduled posts onto dates
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> Carousel</span>
          <span className="flex items-center gap-1"><Film className="w-3 h-3" /> Reel</span>
          <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Static</span>
        </div>
      </div>

      {/* Post Calendar */}
      <CalendarSection
        title="Post"
        icon={LayoutGrid}
        scheduled={data.scheduled_posts}
        unscheduled={data.unscheduled_posts}
        onDrop={handleDrop}
        onUnschedule={handleUnschedule}
        rangeDates={rangeDates}
      />

      {/* Story Calendar */}
      <CalendarSection
        title="Story"
        icon={BookOpen}
        scheduled={data.scheduled_stories}
        unscheduled={data.unscheduled_stories}
        onDrop={handleDrop}
        onUnschedule={handleUnschedule}
        rangeDates={rangeDates}
      />
    </div>
  );
};

export default ContentCalendar;
